/**
 * Permanent redirects for the retired Action View and Playhouse apps (plan
 * 2026-10-13-001 U3, R1/R5). Registered in `AppRouter.tsx` before the
 * `/tools/*` and `/flow/*` catch-alls.
 *
 * DO NOT DELETE: Clay `generate_message` hardcodes `/flow/action-view?action=`
 * (`src/services/flow/generate-message-workflow-definition.ts:94`) and Slack
 * messages in the wild carry it.
 *
 * The `?action=` param name is FROZEN — that out-of-repo Clay routine
 * hardcodes it, and every Slack "Open in Terra" button sent before the
 * Signals-tab cutover carries `?action=<Signal_Action__c id>&source=…`. This
 * file is now the only reader of it: the raw SFDC id is minted into the
 * Signals tab's prefixed `?signal=` id via `recordSignalId` (never a
 * hand-written prefix), and every other param (`source` for click
 * attribution, `impersonate`, …) rides along untouched.
 *
 * The id is normalized to its 18-char form first (`convertTo18CharId`):
 * `terra_signals.signal_id` is minted from the mirror's 18-char
 * `Signal_Action__c` id, so a 15-char `?action=` would otherwise resolve to no
 * row and toast "isn't in Terra any more". This redirect is the only place a
 * hand-typed or Clay-supplied id enters; every other `signal_action:` id in
 * the app comes from the mirror already 18-char.
 *
 * `impersonate` must survive every replace-Navigate here, for the same reason
 * `RootRedirect` (`AppRouter.tsx`) forwards it: dropping it silently switches
 * whose data a non-prod session sees.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { convertTo18CharId } from '@shared/flow/sfdc-id';
import { recordSignalId, SIGNAL_ID_KIND } from '@shared/schemas/terra-signals';
import { COMMAND_CENTER_BASE, flowHome } from '../../../../../mini-apps/_shared/flow-links';

/** A 15- or 18-char Salesforce record id — anything else is not a signal id. */
const SFDC_ID_RE = /^[A-Za-z0-9]{15,18}$/;

/** Where the Ranker settings (formerly `/flow/action-view/settings`) live now. */
const RANKER_ADMIN_PATH = '/admin/configuration/ranker';

/**
 * `?action=<id>&…` → `/flow/command-center?tab=signals&signal=<prefixed id>&…`.
 * A missing or malformed `action` still lands on the Signals tab, just with no
 * row pinned.
 */
export function legacyActionViewTarget(search: string): string {
  const params = new URLSearchParams(search);
  const action = params.get('action');
  params.delete('action');
  params.set('tab', 'signals');
  if (action && SFDC_ID_RE.test(action)) {
    params.set('signal', recordSignalId(SIGNAL_ID_KIND.signal_action, convertTo18CharId(action)));
  }
  return `${COMMAND_CENTER_BASE}?${params.toString()}`;
}

/** Appends `impersonate` (only) to `base`, matching `RootRedirect`'s encoding. */
function withImpersonate(base: string, search: string): string {
  const impersonate = new URLSearchParams(search).get('impersonate');
  if (!impersonate) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}impersonate=${encodeURIComponent(impersonate)}`;
}

/** Any Playhouse URL → the Plays tab (decision 8: `impersonate` forwarded). */
export function legacyPlayhouseTarget(search: string): string {
  return withImpersonate(flowHome('plays'), search);
}

/** `/flow|/tools/action-view/settings` → Admin > Configuration > Ranker. */
export function legacyRankerSettingsTarget(search: string): string {
  return withImpersonate(RANKER_ADMIN_PATH, search);
}

export function LegacyActionViewRedirect() {
  const { search } = useLocation();
  return <Navigate to={legacyActionViewTarget(search)} replace />;
}

export function LegacyPlayhouseRedirect() {
  const { search } = useLocation();
  return <Navigate to={legacyPlayhouseTarget(search)} replace />;
}

export function LegacyRankerSettingsRedirect() {
  const { search } = useLocation();
  return <Navigate to={legacyRankerSettingsTarget(search)} replace />;
}
